import { Field } from 'formik';
import React, { FC } from 'react';
import AnimateHeight from 'react-animate-height';
import { useTranslation } from 'react-i18next';
import { StyledCheckbox } from '../checkbox';
import * as StyledFormikCheckboxTile from './formikCheckboxTile.styles';

const CheckboxTile = ({
	field: { value, onChange, onBlur, groupId },
	id,
	label,
	className,
	setFieldValue,
	values,
	Icon,
	price,
	name,
	renderTopRight,
	children,
	active,
	...props
}) => {
	const { t } = useTranslation('common');
	return (
		<StyledFormikCheckboxTile.Wrapper
			checked={value}
			tabIndex="0"
			onKeyDown={e => {
				if (e.keyCode === 32 || e.keyCode === 13) {
					e.preventDefault();
					const valueArray = values ? [...values] : [];

					if (!value) valueArray.push(id);
					else valueArray.splice(valueArray.indexOf(id), 1);
					setFieldValue(groupId, valueArray);
				}
			}}>
			<input
				name={name}
				id={id}
				type="checkbox"
				value={id}
				checked={value}
				onChange={onChange}
				onBlur={onBlur}
				{...props}
			/>
			<StyledFormikCheckboxTile.CheckboxTileLabel htmlFor={id} active={active}>
				<StyledFormikCheckboxTile.TileCheckbox checked={value} />
				<StyledFormikCheckboxTile.Top>
					<StyledFormikCheckboxTile.TopLeft>
						{Icon && (
							<StyledFormikCheckboxTile.IconWrapper checked={value}>
								<Icon />
							</StyledFormikCheckboxTile.IconWrapper>
						)}
						<StyledFormikCheckboxTile.LabelText>{label}</StyledFormikCheckboxTile.LabelText>
					</StyledFormikCheckboxTile.TopLeft>
					{price && (
						<StyledFormikCheckboxTile.PriceWrapper>
							<StyledFormikCheckboxTile.PriceLabel>{t('price')}</StyledFormikCheckboxTile.PriceLabel>
							<StyledFormikCheckboxTile.PriceValue>{price}</StyledFormikCheckboxTile.PriceValue>
						</StyledFormikCheckboxTile.PriceWrapper>
					)}
					{renderTopRight && renderTopRight(props)}
				</StyledFormikCheckboxTile.Top>
			</StyledFormikCheckboxTile.CheckboxTileLabel>
			<AnimateHeight height={active ? 'auto' : 0}>
				<StyledFormikCheckboxTile.ChildWrapper>{children}</StyledFormikCheckboxTile.ChildWrapper>
			</AnimateHeight>
		</StyledFormikCheckboxTile.Wrapper>
	);
};

const SimpleCheckboxTile = ({
	field: { value, onChange, onBlur, groupId },
	id,
	label,
	className,
	setFieldValue,
	values,
	Icon,
	price,
	name,
	renderTopRight,
	children,
	active,
	disabled,
	disabledMessage,
	...props
}) => {
	return (
		<StyledFormikCheckboxTile.SimpleWrapper
			disabled={disabled}
			onKeyDown={e => {
				if (disabled) return;
				if (e.keyCode === 32 || e.keyCode === 13) {
					e.preventDefault();
					const valueArray = values ? [...values] : [];

					if (!value) valueArray.push(id);
					else valueArray.splice(valueArray.indexOf(id), 1);
					setFieldValue(groupId, valueArray);
				}
			}}>
			<input
				name={name}
				id={id}
				type="checkbox"
				value={id}
				checked={value}
				onChange={disabled ? undefined : onChange}
				onBlur={onBlur}
				disabled={disabled}
				{...props}
			/>
			<label htmlFor={disabled ? undefined : id}>
				<div>
					<StyledCheckbox checked={value} />
				</div>
				<div>
					<div>{label + ' (' + price + ')'}</div>
					<div>{children}</div>
				</div>
			</label>
			{disabled && disabledMessage && (
				<StyledFormikCheckboxTile.DisabledMessage>{disabledMessage}</StyledFormikCheckboxTile.DisabledMessage>
			)}
		</StyledFormikCheckboxTile.SimpleWrapper>
	);
};

interface Props {
	id: string;
	name: string;
	label: string;
	icon?: any;
	price?: string;
	active: boolean;
	renderTopRight?: any;
	setFieldValue: any;
	values: string[];
	children?: React.ReactNode;
	disabled?: boolean;
	disabledMessage?: string;
}

export const SimpleFormikCheckboxTile: FC<Props> = ({ icon, price, disabled, disabledMessage, ...props }) => {
	return <Field component={SimpleCheckboxTile} Icon={icon} price={price} disabled={disabled} disabledMessage={disabledMessage} {...props} />;
};

const FormikCheckboxTile: FC<Props> = ({ icon, price, ...props }) => {
	return <Field component={CheckboxTile} Icon={icon} price={price} {...props} />;
};

export default FormikCheckboxTile;
